import { Injectable } from "@angular/core";
import { Location } from "@angular/common";
import { Router } from "@angular/router";
import { GlobalSettingsService } from "@core/globalSettings.service";
import { TimberService } from "@logging/timber.service";
import { CommunicationChannels, NavigationInfo, ShellCommunicationEvents } from "@shared/generalInterfaces";
import { Subscription, map, take, tap, of } from "rxjs";
import { timer } from "rxjs/internal/observable/timer";
import { Consts } from '@shared/consts';
import { OrdersService } from "app/doctors/orders/orders.service";
import { getCopy } from "app/extensions/map-extensions";
import { KnownComponentsNames } from "@core/components-names";
import { NavigationStore } from "./state/navigation-store";
import { NavigationQuery } from "./state/navigation-query";
import { CommunicationService } from "app/services/client-communication/communication.service";
import { EventName } from "@nodeModules/@itero/itero-client-communication";
import { isStandardRemoteApplication } from "app/remote-application/helpers/is-standard-remote-application";
import { STANDARD_REMOTE_URL_PREFIX } from "app/remote-application/consts";
import { ImageHubOverlayService } from '../../image-hub-overlay/image-hub-overlay.service';
import { BiFeatureWrapperService } from "@logging/bi-feature-wrapper.service";
import { SEARCH_FILTER_STORAGE_KEY } from "assets/consts";

@Injectable({ providedIn: 'root' })
export class ApplicationNavigationService {
	readonly domain = window.location.origin;
	private intervalForClosingApp = 10000;
	private closingAppSubscriptions = new Map<string, Subscription>();
	closingTimerApps: KnownComponentsNames[] = [
		KnownComponentsNames.AohsComponent,
		KnownComponentsNames.IteroAxiWrapper,
		KnownComponentsNames.AxiWrapper,
		KnownComponentsNames.IteroViewerPro,
		Consts.visitReportAppId
	];

	constructor(
		private router: Router,
		private location: Location,
		private globalSettingsService: GlobalSettingsService,
		private timberService: TimberService,
		private ordersService: OrdersService,
		private communicationService: CommunicationService,
		private store: NavigationStore,
		private readonly query: NavigationQuery,
		private readonly imageHubOverlayService: ImageHubOverlayService,
		private readonly biFeatureWrapperService: BiFeatureWrapperService
	) { }

	reset(): void {
		this.store.reset();
	}

	//transition: NavigationInfo
	pushTransition(transition: NavigationInfo): void {
		this.store.pushTransition(transition);
	}

	popTransition(): NavigationInfo {
		const lastTransition = this.peekTransition();
		this.store.removeTransition();
		return lastTransition;
	}

	peekTransition(): NavigationInfo {
		return this.query.peekTransition();
	}

	startClosingAppTimer(transition: NavigationInfo) {
		const subscription = timer(this.intervalForClosingApp)
			.pipe(
				take(1),
				tap((_) => this.closingAppSubscriptions.delete(transition.navigatedToAppId))
			)
			.subscribe(() => {
				this.router.navigateByUrl(transition.urlFrom, { state: this.buildBackNavigationState(transition) });
			});
		this.closingAppSubscriptions.set(transition.navigatedToAppId, subscription);
	}

	stopClosingAppTimer(appId: string) {
		this.closingAppSubscriptions.get(appId)?.unsubscribe();
		this.closingAppSubscriptions.delete(appId);
	}

	navigateToHomePageWhenTransitionNotFound(backFromAppId: string) {
		this.timberService.error(`Navigation back from ${backFromAppId} failed.`, { module: 'ApplicationNavigationService' });
		this.navigateToHomePage();
	}

	navigateToHomePageWhenStackIsEmpty() {
		this.timberService.error(`Navigation stack is empty when user clicked back button.`, { module: 'ApplicationNavigationService' });
		this.navigateToHomePage();
	}

	addToClosingTimerApps(appId: KnownComponentsNames) {
		if (!this.closingTimerApps.includes(appId)) {
			this.closingTimerApps.push(appId);
		}
	}

	removeFromClosingTimerApps(appId: KnownComponentsNames) {
		const index = this.closingTimerApps.indexOf(appId);
		if (index > -1) {
			this.closingTimerApps.splice(index, 1);
		}
	}

	handleBackClicked() {
		const currentTransition = this.peekTransition();
		if (!currentTransition) {
			this.navigateToHomePageWhenStackIsEmpty();
		} else if (currentTransition.navigatedFromAppId.includes('login')) {
			this.reset();
			this.navigateToHomePage();
		} else if (this.closingTimerApps.includes(currentTransition.navigatedToAppId)) {
			this.startClosingAppTimer(currentTransition);
			this.communicationService.publishEvent(
                CommunicationChannels.ApplicationChannel,
                'app-shell',
                ShellCommunicationEvents.PrepareToClose,
                [currentTransition.navigatedToAppId]
            );
		// all the logic around ImageHubOverlayService could be removed after TR-73441 is implemented and utilized.
		} else if (this.shouldCloseImageHubOverlay(currentTransition)) {
			this.closeImageHubOverlayAndNavigateBack(currentTransition);
		} else {
			// In case if the navigation is within the same app and the MFE is full separate client application - launched in its own angular runtime
			// or another from angular the routing inside the host could not affect MFE routing
			// in that case we need to send the event to the MFE to handle the navigation
			if (currentTransition.navigatedFromAppId === currentTransition.navigatedToAppId) {
				this.communicationService.publishEvent(
					CommunicationChannels.ApplicationChannel,
					'app-shell',
					EventName.applyNavigationState,
					[currentTransition.navigatedFromAppId],
					{
						navigateFromAppId: currentTransition.navigatedFromAppId,
						navigateToAppId: currentTransition.navigatedToAppId,
						urlTo: `${this.domain}${currentTransition.urlFrom}`,
					}
				);
				if (isStandardRemoteApplication(currentTransition.navigatedFromAppId)) {
					this.store.removeTransition();
					return;
				}
			}
			this.router.navigateByUrl(currentTransition.urlFrom, { state: this.buildBackNavigationState(currentTransition) });
		}
	}

	navigateToStandardRemoteApplication(appId: string, appParameters?: Record<string, unknown>) {
		if (this.imageHubOverlayService.shouldShowAsOverlay(appId)) {
			const hostAppId = this.imageHubOverlayService.openOverlay(appParameters);
			this.pushTransition({
				navigatedFromAppId: this.biFeatureWrapperService.convertNames(hostAppId),
				urlFrom: this.router.url,
				navigatedToAppId: this.biFeatureWrapperService.convertNames(appId),
				appMetadata: JSON.stringify(appParameters)
			});
			return Promise.resolve(true);
		}
		return this.router.navigate([STANDARD_REMOTE_URL_PREFIX, appId], appParameters ? { state: appParameters } : undefined);
	}

	navigate(transition: NavigationInfo) {
		if (isStandardRemoteApplication(transition.navigatedToAppId)) {
			this.navigateToStandardRemoteApplication(
				transition.navigatedToAppId,
				transition.appMetadata != null ? JSON.parse(transition.appMetadata) : undefined
			);
			return;
		}

		const navigationCommands = Consts.NavigationCommands[getCopy](transition.navigatedToAppId);
		this.addCommands(transition.navigatedToAppId, navigationCommands, transition.appMetadata)
			.pipe(
				tap((commands) => {
					const queryParams = this.getQueryParams(transition);
					if (queryParams) {
						this.router.navigate(commands, { queryParams: queryParams });
					} else this.router.navigate(commands);
				}),
				take(1)
			)
			.subscribe();
	}

	navigateBack(backFromAppId: string) {
		const transition = this.peekTransition();
		if (transition) {
			this.stopClosingAppTimer(transition.navigatedToAppId);
			// all the logic around ImageHubOverlayService could be removed after TR-73441 is implemented and utilized.
			if (this.shouldCloseImageHubOverlay(transition)) {
				this.closeImageHubOverlayAndNavigateBack(transition);
			} else {
				this.router.navigateByUrl(transition.urlFrom, { state: this.buildBackNavigationState(transition) });
			}
		} else {
			this.navigateToHomePageWhenTransitionNotFound(backFromAppId);
		}
	}

	navigateToHomePage() {
		const roleType = this.globalSettingsService.rolePath();
		sessionStorage.setItem(SEARCH_FILTER_STORAGE_KEY, '');
		this.router.navigate([`${roleType}/home`]);
	}

	private addCommands(appId: string, commands: any, stringAppMetadata: string) {
		switch (appId) {
			case 'iosim': {
				const appMetadata = JSON.parse(stringAppMetadata);
				return this.ordersService.getOrderCode(appMetadata.orderId).pipe(
					take(1),
					map((orderCode) => {
						commands.push(orderCode);
						commands.push(appMetadata.orderId);
						return commands;
					})
				);
			}
			default:
				return of(commands);
		}
	}

	private getQueryParams(transition: NavigationInfo) {
		switch (transition.navigatedToAppId) {
			case 'iosim': {
				return { returnUrl: transition.urlFrom }; ///check here as removed returnUrl
			}
			case 'itero-visit-reports': {
				return { returnUrl: transition.urlFrom };
			}
			case 'axi-wrapper': {
				return { returnUrl: transition.urlFrom };
			}
			default:
				return {};
		}
	}

	// all the logic around ImageHubOverlayService could be removed after TR-73441 is implemented and utilized.
	private shouldCloseImageHubOverlay(transition: NavigationInfo): boolean {
		return (
			transition.navigatedToAppId.includes(KnownComponentsNames.ImageHub) &&
			this.imageHubOverlayService.isOverlayOpen &&
			transition.navigatedFromAppId !== transition.navigatedToAppId
		);
	}

	// all the logic around ImageHubOverlayService could be removed after TR-73441 is implemented and utilized.
	private closeImageHubOverlayAndNavigateBack(transition: NavigationInfo): void {
		this.imageHubOverlayService.closeOverlay();
		this.location.replaceState(transition.urlFrom);
		this.store.removeTransition();
	}

	private buildBackNavigationState(transition: NavigationInfo): Record<string, unknown> {
		const state: Record<string, unknown> = { isBackNavigation: true };
		if (isStandardRemoteApplication(transition.navigatedFromAppId)) {
			const originalEntry = this.findLastNavigationTo(transition.navigatedFromAppId);
			if (originalEntry?.appMetadata) {
				try {
					Object.assign(state, JSON.parse(originalEntry.appMetadata));
				} catch { /* ignore malformed metadata */ }
			}
		}
		return state;
	}

	private findLastNavigationTo(appId: string): NavigationInfo | undefined {
		const stack = this.query.transitionsStack;
		for (let i = stack.length - 1; i >= 0; i--) {
			if (stack[i].navigatedToAppId === appId) {
				return stack[i];
			}
		}
		return undefined;
	}
}